'use client';

/**
 * React Query hooks for i18n dashboard feature.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchDashboardStats,
  fetchDashboardQueues,
  fetchDashboardCharts,
  fetchActivityFeed,
  fetchMyActivity,
  fetchDeprecatedKeysInDatabase,
  fetchDeprecatedKeyRecords,
  fetchLatestKeyReportExecution,
  fetchKeyReportHistory,
  triggerKeyReport,
  fetchKeyReportKeys,
} from './api';

// ---------- Query Key Factory ----------

export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  queues: () => [...dashboardKeys.all, 'queues'] as const,
  charts: () => [...dashboardKeys.all, 'charts'] as const,
  activity: (limit?: number) => [...dashboardKeys.all, 'activity', limit] as const,
  myActivity: () => [...dashboardKeys.all, 'my-activity'] as const,
  deprecatedKeys: () => [...dashboardKeys.all, 'deprecated-keys'] as const,
  deprecatedKeyRecords: (table: string, column: string, page: number, pageSize: number) =>
    [...dashboardKeys.all, 'deprecated-key-records', table, column, page, pageSize] as const,
  keyReportLatest: (status?: string) => [...dashboardKeys.all, 'key-report', 'latest', status] as const,
  keyReportHistory: (page: number, pageSize: number) => [...dashboardKeys.all, 'key-report', 'history', page, pageSize] as const,
  keyReportKeys: (executionUuid: string, category: string, page: number, pageSize: number) =>
    [...dashboardKeys.all, 'key-report', 'keys', executionUuid, category, page, pageSize] as const,
};

// ---------- Query Hooks ----------

export function useDashboardStats(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.stats(),
    queryFn: fetchDashboardStats,
    staleTime: 0,
    enabled,
  });
}

export function useDashboardQueues(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.queues(),
    queryFn: fetchDashboardQueues,
    staleTime: 0,
    enabled,
  });
}

export function useDashboardCharts(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.charts(),
    queryFn: fetchDashboardCharts,
    staleTime: 0,
    enabled,
  });
}

export function useActivityFeed(limit = 10, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.activity(limit),
    queryFn: () => fetchActivityFeed(limit),
    staleTime: 0,
    enabled,
  });
}

export function useMyActivity(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.myActivity(),
    queryFn: fetchMyActivity,
    staleTime: 0,
    enabled,
  });
}

export function useDeprecatedKeysInDatabase(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.deprecatedKeys(),
    queryFn: fetchDeprecatedKeysInDatabase,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useDeprecatedKeyRecords(
  tableName: string,
  columnName: string,
  page = 1,
  pageSize = 20,
  enabled = true
) {
  return useQuery({
    queryKey: dashboardKeys.deprecatedKeyRecords(tableName, columnName, page, pageSize),
    queryFn: () => fetchDeprecatedKeyRecords(tableName, columnName, page, pageSize),
    staleTime: 5 * 60_000,
    enabled,
  });
}

// ---------- Key Report Hooks ----------

export function useKeyReportStatus(enabled = true) {
  const { data, ...rest } = useQuery({
    queryKey: dashboardKeys.keyReportLatest(),
    queryFn: () => fetchLatestKeyReportExecution(),
    staleTime: 0,
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 10_000 : false;
    },
  });
  return { data, ...rest };
}

export function useLatestSuccessfulKeyReport(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.keyReportLatest('succeeded'),
    queryFn: () => fetchLatestKeyReportExecution('succeeded'),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useKeyReportHistory(page = 1, pageSize = 10, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.keyReportHistory(page, pageSize),
    queryFn: () => fetchKeyReportHistory(page, pageSize),
    staleTime: 0,
    enabled,
  });
}

export function useTriggerKeyReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetBranch?: string) => triggerKeyReport(targetBranch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.keyReportLatest() });
      queryClient.invalidateQueries({ queryKey: [...dashboardKeys.all, 'key-report', 'history'] });
    },
  });
}

export function useKeyReportKeys(
  executionUuid: string | null,
  category: string,
  page = 1,
  pageSize = 20,
  enabled = true
) {
  return useQuery({
    queryKey: dashboardKeys.keyReportKeys(executionUuid ?? '', category, page, pageSize),
    queryFn: () => fetchKeyReportKeys(executionUuid!, category, page, pageSize),
    staleTime: 5 * 60_000,
    enabled: enabled && !!executionUuid,
  });
}
